import { APP_ROUTES } from '@/common/constants'
import { isMobile } from '@/common/utils/utils'
import { AccountStatus, AccountType } from '@/common/types'
import type { User, Account } from '@/common/types'

/**
 * Determines the appropriate onboarding redirect route based on user state.
 * Pure function for easy testing and memoization.
 *
 * @returns Route to redirect to, or null if no redirect needed
 */
export function getOnboardingRedirect(
    user: User | null,
    isAccountSwitching: boolean,
    accounts: Account[],
    primaryAccount: Account | null,
    account: Account | null,
): string | null {
    // Don't redirect during account switching or if no user
    if (isAccountSwitching || !user) return null

    const isMobileDevice = isMobile()

    // Check required user fields in order of onboarding flow
    if (!user.salutation) {
        return isMobileDevice ? APP_ROUTES.NAME : APP_ROUTES.ABOUT_YOU
    }

    if (!user.addresses?.residentialAddress) {
        return isMobileDevice ? APP_ROUTES.ADDRESS : APP_ROUTES.ABOUT_YOU
    }

    if (!user.mobile) {
        return isMobileDevice ? APP_ROUTES.MOBILE_NUMBER : APP_ROUTES.ABOUT_YOU
    }

    if (!user.occupation) {
        return APP_ROUTES.SURVEY
    }

    if (account && account.termsAccepted === false) {
        if (account.accountType === AccountType.ADVISER) {
            return APP_ROUTES.TERMS_AND_CONDITIONS_UPDATE
        }

        return account.accountStatus === AccountStatus.KYC_NOT_STARTED
            ? APP_ROUTES.TERMS_AND_CONDITIONS
            : APP_ROUTES.TERMS_AND_CONDITIONS_UPDATE
    }

    // Check if user has no accounts (primary or additional) - redirect to account type selection
    const hasAnyAccount = primaryAccount || (accounts && accounts.length > 0)
    if (!hasAnyAccount) {
        return APP_ROUTES.ACCOUNT_TYPE
    }

    // All onboarding checks passed
    return null
}
